import { PDFDocument } from 'pdf-lib';
import { IDocumentShareView } from '../../models/DocumentShare';
import { isImage, isPdf } from './documentShareFormat';

// A4 in PostScript points.
const A4_W = 595.28;
const A4_H = 841.89;

interface LoadedImage {
    dataUrl: string;
    width: number;
    height: number;
}

/**
 * Loads an image and re-encodes it as a PNG data URL via a canvas, returning its
 * natural dimensions for aspect-ratio-preserving placement. Re-encoding normalises
 * every browser-renderable image type (gif/webp/svg/…) to PNG so pdf-lib can embed
 * it. Resolves to null on any failure (404, CORS-tainted canvas, decode error) so
 * export degrades gracefully rather than throwing.
 */
function loadImage(src: string): Promise<LoadedImage | null> {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(null);
                    return;
                }
                ctx.drawImage(img, 0, 0);
                resolve({
                    dataUrl: canvas.toDataURL('image/png'),
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                });
            } catch {
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

/** Fetches an attachment's raw bytes, or null on any failure. */
async function fetchBytes(url: string): Promise<ArrayBuffer | null> {
    try {
        const res = await fetch(url);
        if (!res.ok) return null;
        return await res.arrayBuffer();
    } catch {
        return null;
    }
}

/** Slugifies the document name into a safe PDF filename. */
function fileName(name?: string): string {
    const slug = (name || 'document')
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase();
    return `${slug || 'document'}.pdf`;
}

/** Counts the pages in a PDF byte array; returns 0 if it can't be read. */
export async function pdfPageCount(bytes: Uint8Array): Promise<number> {
    try {
        const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
        return doc.getPageCount();
    } catch {
        return 0;
    }
}

/** Builds the download filename for a document. */
export function pdfFileName(view: IDocumentShareView): string {
    return fileName(view.documentName);
}

/** Triggers a browser download of the given PDF bytes. */
export function downloadPdfBytes(bytes: Uint8Array, name: string): void {
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

/** Embeds an image as a new, fitted-within-margins A4 page. */
async function appendImagePage(pdf: PDFDocument, dataUrl: string, w: number, h: number) {
    const png = await pdf.embedPng(dataUrl);
    const page = pdf.addPage([A4_W, A4_H]);
    const m = 40;
    const scale = Math.min((A4_W - m * 2) / w, (A4_H - m * 2) / h);
    const drawW = w * scale;
    const drawH = h * scale;
    page.drawImage(png, {
        x: (A4_W - drawW) / 2,
        y: (A4_H - drawH) / 2,
        width: drawW,
        height: drawH,
    });
}

/**
 * Builds a combined PDF and returns its bytes: when branded, the brand-styled cover
 * page (page 1) followed by the uploaded document; otherwise the document alone. PDF
 * attachments are appended page-for-page; image attachments are appended as fitted
 * pages. Attachment types that can't be merged client-side (e.g. Word) are skipped.
 *
 * Any branding/cover page is applied server-side, so this only merges the
 * document's attachments as-is.
 *
 * @param view     The shared-document view model.
 * @param urlFor   Resolves a token-scoped download URL for an attachment id.
 */
export async function buildDocumentSharePdf(
    view: IDocumentShareView,
    urlFor?: (attachmentId: number) => string,
): Promise<Uint8Array> {
    const pdf = await PDFDocument.create();

    const attachments = view.attachments ?? [];
    if (urlFor) {
        for (const att of attachments) {
            const bytes = await fetchBytes(urlFor(att.id));
            if (!bytes) continue;
            if (isPdf(att)) {
                try {
                    const src = await PDFDocument.load(bytes, { ignoreEncryption: true });
                    const pages = await pdf.copyPages(src, src.getPageIndices());
                    pages.forEach((p) => pdf.addPage(p));
                } catch {
                    // Unreadable/encrypted PDF — skip it, keep the rest.
                }
            } else if (isImage(att)) {
                const img = await loadImage(urlFor(att.id));
                if (img) await appendImagePage(pdf, img.dataUrl, img.width, img.height);
            }
        }
    }

    return pdf.save();
}
